import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';

import {
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexResponsive,
    ApexStates,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import { MdrDashboardsSeriesDataInterface } from '../../../interfaces/mdr-dashboards-series-data.interface';
import { DashboardTable } from '../../../interfaces/mdr-dashboards-table-data.interface';
import { BarChartDataSource } from '../../../interfaces/mdr-dashboards-chart-area-data.interface';
import { MatTooltip } from '@angular/material/tooltip';
import { get, max } from 'lodash-es';
import { Router } from '@angular/router';
import { MdrDashboardsBarChartService } from './mdr-dashboards-bar-chart.service';
import { ThemeService } from '../../../../../services/theme.service';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    dataLabels: ApexDataLabels;
    labels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    legend: ApexLegend;
    stroke: ApexStroke;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    fill: ApexFill;
    tooltip: ApexTooltip;
    colors: string[];
    states: ApexStates;
};

interface BarchartResponse {
    name: string;
    data: number[];
}

@Component({
    selector: 'app-mdr-dashboards-bar-chart',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './mdr-dashboards-bar-chart.component.html',
    styleUrls: ['./mdr-dashboards-bar-chart.component.scss'],
    standalone: false,
})
export class MdrDashboardsBarChartComponent implements OnInit, OnChanges {
    @ViewChild('barChartRefEmpty') barChartRefEmpty: ElementRef;
    @ViewChild('barChartRef') barChartRef: ElementRef;

    /**  startDate - start date of the chart */
    @Input() public startDate: string;

    /**  endDate - end date of the chart */
    @Input() public endDate: string;

    /**  colors - colors settings for each category of the series */
    @Input() public colors: string[];

    /**  categories - set categories for the series */
    @Input() public categories: (string | string[])[];

    /**  width - widget width */
    @Input() public width: number | string = 300;

    /**  height - widget height */
    @Input() public height: number | string = 300;

    /**  columnWidth - set column width for each category */
    @Input() public columnWidth: number | string = '30%';

    /* bulletType - set the bullet type for the legend. By default, is square */
    @Input() public bulletType = 'square';

    /**  hasMarginTop - enable/disable margin top for the widget. If is true, the margin will be fixed and set inside HTML. */
    @Input() public hasMarginTop = true;

    /**  tableLegend - enable/disable the table legend style for the specific widget */
    @Input() public tableLegend = false;

    /**  scaleLegend - enable/disable the scale legend option for zoom */
    @Input() public scaleLegend = false;

    /**  tooltipWidth - set the width of the tooltip */
    @Input() public tooltipWidth = 220;

    /**  showXaxisLabels - enable/disable X axis labels */
    @Input() public showXaxisLabels = true;

    /**
     * element - element object passed from the tab's component.
     * This object will contain specific settings and custom options for the bar chart widget
     */
    @Input() public element: BarChartDataSource;

    /**  showTooltip - enable/disable tooltip */
    @Input() public showTooltip = true;

    /**  showLegend - enable/disable legend */
    @Input() public showLegend = true;

    /**  yaxisLogarithmicScale - enable/disable logarithmic scale for the Y axis */
    @Input() public yaxisLogarithmicScale = false;

    /**  customBarChartFunnel - enable/disable funnel option */
    @Input() public customBarChartFunnel = false;

    /**
     * @var stacked - enable/disable stacked option for the bar chart widget.
     * If it's true, then each category will be summed up with the previous one.
     */
    @Input() public stacked = true;
    @Input() public horizontal = false;
    @Input() public currentTab = '';
    @Input() public invalidDataToShow = false;
    @Input() public emptyText: string = '';
    public stackedBarChartOptions: Partial<ChartOptions> | any;
    public emptyStackedBarChartOptions: Partial<ChartOptions> | any;
    public disableTooltip = false;
    public maxYaxis: number;

    public legendDisplayedColumns = ['name', 'value'];
    public areaSeries: MdrDashboardsSeriesDataInterface[];
    public areaColors: string[];
    public initialSeries: BarchartResponse[];
    public orgChanged = false;
    public filteredCategories: (string | string[])[] = [];
    /** map Column Index => Drilldown Name */
    public drilldownMapping = {
        0: 'Investigation',
        1: 'Hunt',
    };

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public router: Router,
        private cdr: ChangeDetectorRef,
        private mdrBarChartService: MdrDashboardsBarChartService,
        private themeService: ThemeService,
    ) {}

    @HostListener('window:resize', ['$event'])
    sizeChange(event: Event) {
        /*'quick' fix for the problem where all the charts went missing after browser resizing */
        // @ts-ignore
        let chartRef = new ApexCharts(document.querySelector('#barChartRef'), this.stackedBarChartOptions);
        // @ts-ignore
        let emptyChartRef = new ApexCharts(document.querySelector('#barChartRefEmpty'), this.emptyStackedBarChartOptions);
    }

    public ngOnInit() {
        this.buildStackedChart();
        this.buildEmptyChart();
    }

    public buildStackedChart() {
        this.stackedBarChartOptions = {
            series: [],
            chart: {
                redrawOnWindowResize: true,
                animations: {
                    enabled: false,
                },
                type: 'bar',
                height: this.height,
                width: this.width,
                stacked: this.stacked,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
                events: {
                    click: (event: MouseEvent, chartContext: any, config: any) => {
                        const pointClicked = config.dataPointIndex;

                        if (!this.element.drilldown?.show) {
                            return;
                        }

                        let url = '';
                        let state: { [p: string]: any } = {};

                        /** first column - Environment telemetry */
                        if (pointClicked === 0) {
                            url = 'investigations/browse';
                            state['detection_name'] = 'Gen:Illusion.ML.Skyline.9.10101';
                            state['lco_drilldown'] = true;
                        }

                        /** second column - Suspicious events */
                        if (pointClicked === 1) {
                            url = 'threat-hunts/browse';
                            state['type'] = 'Risk-Based';
                        }

                        this.router.navigateByUrl(url, { state: state });
                    },
                    dataPointMouseEnter: (event: any) => {
                        if (this.element.drilldown?.show) {
                            event.target.style.cursor = 'pointer';
                        }
                    },
                },
            },
            tooltip: {
                theme: this.themeService.isDarkMode ? 'dark' : 'light',
                enabled: this.showTooltip,
                shared: true,
                intersect: false,
                custom: ({
                    series,
                    seriesIndex,
                    dataPointIndex,
                    w,
                }: {
                    series: [];
                    seriesIndex: number;
                    dataPointIndex: number;
                    w: any;
                }) => {
                    let tempSeries: any = [];
                    let seriesNames: any = [];
                    if (this.customBarChartFunnel) {
                        this.initialSeries.map((item: BarchartResponse) => {
                            tempSeries.push(item.data);
                            seriesNames.push(item.name);
                        });
                        series = tempSeries;
                    }
                    const hoverXaxis = series[seriesIndex][dataPointIndex];
                    let label;
                    label = w.globals.labels[dataPointIndex];
                    const hoverIndexes = series.map((seriesX: any) => {
                        return seriesX.findIndex((xData: any) => xData === hoverXaxis);
                    });
                    let columnTotal = 0;
                    let hoverList = '';
                    hoverIndexes.forEach((hoverIndex: number, seriesEachIndex: number) => {
                        if (this.customBarChartFunnel) {
                            hoverList =
                                `${
                                    series[seriesEachIndex][dataPointIndex] !== null && series[seriesEachIndex][dataPointIndex] !== 0
                                        ? `<div class='gz-data-item'>
                                        <span class='${this.bulletType}-bullet-legend' style='background: ${w.globals.colors[seriesEachIndex]}'></span><br />
                                        <div class='gz-data-item--title'>${seriesNames[seriesEachIndex]}</div>
                                        <div class='gz-data-item--value'>${series[seriesEachIndex][dataPointIndex]}
                                        </div>
                                    </div>`
                                        : ``
                                }
                            ` + hoverList;
                        } else {
                            hoverList =
                                `${
                                    series[seriesEachIndex][dataPointIndex] !== 0
                                        ? `<div class='gz-data-item'>
                                        <span class='${this.bulletType}-bullet-legend' style='background: ${w.globals.colors[seriesEachIndex]}'></span><br />
                                        <div class='gz-data-item--title'>${w.globals.seriesNames[seriesEachIndex]}</div>
                                        <div class='gz-data-item--value'>
                                            ${series[seriesEachIndex][dataPointIndex]}
                                        </div>
                                    </div>
                            `
                                        : ''
                                }` + hoverList;
                        }
                        columnTotal += series[seriesEachIndex][dataPointIndex];
                    });

                    if (this.customBarChartFunnel) {
                        if (dataPointIndex === 0 || dataPointIndex === 4) {
                            return `<div class='chart-tooltip-container'>
                                        <div class='gz-data-item' style='min-width: ${this.tooltipWidth}px'>
                                            <div class='gz-data-item--title'>${label.toString().replace(',', ' ')}</div>
                                            <div class='gz-data-item--value'>
                                                ${w.globals.stackedSeriesTotals[dataPointIndex]}
                                            </div>
                                        </div>
                                    </div>`;
                        }
                    }

                    return `<div class='chart-tooltip-container' style='min-width: ${this.tooltipWidth}px'>
                                ${
                                    this.showXaxisLabels
                                        ? `<div class='chart-tooltip-title'>${label?.toString().replace(',', ' ')}</div>`
                                        : ``
                                }
                                ${hoverList}
                            </div>`;
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            plotOptions: {
                bar: {
                    horizontal: this.horizontal,
                    dataLabels: {
                        position: 'top',
                        hideOverflowingLabels: false,
                    },
                    columnWidth: this.columnWidth,
                },
            },
            colors: this.colors,
            dataLabels: {
                enabled: true,
                offsetY: -20,
                style: {
                    fontSize: '12px',
                    colors: this.themeService.isDarkMode ? ['#FFFFFF40'] : ['#0000001F'],
                    fontWeight: 900,
                },
                formatter: function (
                    value: any,
                    { seriesIndex, dataPointIndex, w }: { seriesIndex: number; dataPointIndex: number; w: any },
                ) {
                    if (seriesIndex === w.config.series.length - 1) {
                        return this.mdrDashboardsService.intToString(
                            w.globals.stackedSeriesTotals[dataPointIndex],
                            1000,
                            this.element.approximate,
                        );
                    }
                    return '';
                }.bind(this),
            },
            xaxis: {
                type: 'category',
                categories: this.categories,
                labels: {
                    show: this.showXaxisLabels,
                    rotate: 0,
                    style: {
                        colors: this.themeService.isDarkMode ? '#FFFFFFCC' : '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                },
                axisBorder: {
                    show: true,
                    color: '#808080',
                    height: 0,
                    width: '100%',
                },
            },
            yaxis: {
                logarithmic: this.yaxisLogarithmicScale,
                forceNiceScale: true,
                max: function (val: number) {
                    return Math.floor(val) + 1;
                },
                labels: {
                    style: {
                        colors: this.themeService.isDarkMode ? '#FFFFFFCC' : '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                    formatter: function (val: number) {
                        if (val === Infinity) {
                            return 1;
                        }
                        return this.mdrDashboardsService.intToString(val ? Number(val.toFixed(0)) : 0, 1000, this.element.approximate);
                    }.bind(this),
                },
            },
            legend: {
                show: false,
                formatter: function (seriesName: string, opts: any) {
                    return opts.w.globals.seriesTotals[opts.seriesIndex] > 0
                        ? [seriesName, ': ', opts.w.globals.seriesTotals[opts.seriesIndex]]
                        : '';
                },
            },
            fill: {
                opacity: 1,
            },
            states: {
                hover: {
                    filter: {
                        type: 'none',
                    },
                },
                active: {
                    filter: {
                        type: 'none',
                    },
                },
            },
        };
    }

    public buildEmptyChart() {
        this.emptyStackedBarChartOptions = {
            series: [],
            chart: {
                type: 'bar',
                height: this.height,
                width: this.width,
                stacked: true,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: false,
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            plotOptions: {
                bar: {
                    horizontal: this.horizontal,
                    dataLabels: {
                        position: 'top',
                    },
                    columnWidth: this.columnWidth,
                },
            },
            colors: [this.themeService.isDarkMode ? '#FFFFFF33' : '#0000004D'],
            dataLabels: {
                enabled: true,
                offsetY: -20,
                style: {
                    fontSize: '12px',
                    colors: this.themeService.isDarkMode ? ['#FFFFFF40'] : ['#0000001F'],
                    fontWeight: 900,
                },
                formatter: function (
                    value: any,
                    { seriesIndex, dataPointIndex, w }: { seriesIndex: number; dataPointIndex: number; w: any },
                ) {
                    let indices = w.config.series.map((item: any, i: number) => i);

                    indices = indices.filter(
                        (i: number) =>
                            !w.globals.collapsedSeriesIndices.includes(i) && get(w.config.series, `${i}.data.${dataPointIndex}`) > 0,
                    );

                    if (seriesIndex == max(indices)) {
                        return '- -';
                    }

                    return '';
                },
            },
            tooltip: {
                enabled: false,
            },
            states: {
                hover: {
                    filter: {
                        type: 'none',
                    },
                },
                active: {
                    filter: {
                        type: 'none',
                    },
                },
            },
            xaxis: {
                type: 'category',
                categories: this.categories,
                labels: {
                    show: this.showXaxisLabels,
                    rotate: 0,
                    style: {
                        colors: this.themeService.isDarkMode ? '#FFFFFFCC' : '#00000080',
                        fontSize: '10px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 400,
                    },
                },
                axisBorder: {
                    show: true,
                    color: '#E2E2E2',
                    height: 0,
                    width: '100%',
                },
            },
            yaxis: {
                max: 9,
                labels: {
                    show: false,
                },
            },
            legend: {
                show: false,
            },
            fill: {
                opacity: 1,
            },
        };
    }

    /**
     * This function iterates through all the categories from the series and calculate the sum for the legend
     * @param series - data series to be processed
     * @param colors - colors settings for each category
     * @param enableEmptyValue - enable/disable empty value representation for the specific row from the legend
     */
    public calculateLegendTotal(series: MdrDashboardsSeriesDataInterface[], colors: string[], enableEmptyValue = false): DashboardTable[] {
        let data: DashboardTable[] = [];

        series.forEach((element: { name: string; data: [] }, index: number) => {
            const s = element.data.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
            // @ts-ignore
            if ((s > 0 && !enableEmptyValue) || enableEmptyValue) {
                let temp: DashboardTable = {
                    name: '',
                    value: 0,
                };
                let sum = 0;

                element.data.forEach((value: number) => {
                    sum += value;
                });

                temp.name = element.name;
                temp.value = enableEmptyValue ? '- -' : this.mdrDashboardsService.intToString(sum, 1000, this.element.approximate);
                temp.color = enableEmptyValue ? '#E2E2E2' : colors[index];
                data.unshift(<DashboardTable>temp);
            }
        });

        return data;
    }

    /**
     * Function to convert stacked datasource to single category datasource.
     * In order to achieve this, we want to calculate the sum for each category.
     * @param series - data series to be processed
     */
    public convertStackedToSingleBar(series: BarchartResponse[]) {
        for (let i = this.mdrBarChartService.barChartEmptyCategories.length - 1; i >= 0; i--) {
            this.categories.splice(this.mdrBarChartService.barChartEmptyCategories[i], 1);
        }

        this.stackedBarChartOptions.xaxis.categories = this.categories;
        this.stackedBarChartOptions.plotOptions.bar.columnWidth = '35%';
        if (this.categories.length === 1) {
            this.stackedBarChartOptions.plotOptions.bar.columnWidth = '20%';
        }
        if (!this.customBarChartFunnel) {
            return series;
        }

        this.initialSeries = series;
        let sumArr: any[] = [];
        for (let i = 0; i < series[0].data.length; i++) {
            let sum = 0;
            for (let j = 0; j < series.length; j++) {
                sum += series[j].data[i];
            }
            sumArr.push(sum > 0 ? sum : null);
        }
        return [
            {
                name: 'Events',
                data: sumArr,
            },
        ];
    }

    /**
     * Enable/disable tooltip for a specific text according to scrollWidth and clientWidth.
     * @param event - mouse event
     * @param tooltip - tooltip object to be shown
     * @param findClosest - closest class or element to look into
     */
    public checkInputClientVisibility(event: MouseEvent, tooltip: MatTooltip, findClosest = '') {
        let target: Element | null = event.target as Element;
        if (findClosest) {
            target = target.closest(findClosest);
        }

        if (target) {
            this.disableTooltip = !(target.scrollWidth > target.clientWidth);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes &&
            changes.currentTab &&
            changes.currentTab.currentValue &&
            changes.currentTab.currentValue !== changes.currentTab.previousValue
        ) {
            this.orgChanged = true;
            this.buildStackedChart();
            this.buildEmptyChart();
        }

        if (changes.element && changes.element.currentValue && changes.element.currentValue !== changes.element.previousValue) {
            this.element = changes.element.currentValue;
            this.orgChanged = true;
        }
    }
}
